/**
 * KnowIt adapter — the third implementation of ApiClient, alongside FakeApi and HttpApi.
 *
 * The platform speaks Outline-style RPC (POST /api/documents.list, `{ data, policies,
 * pagination }`) rather than REST, so this file is the whole translation: endpoint shapes,
 * field names, and — most importantly — permissions.
 *
 * The server already returns `policies: [{ id, abilities }]` per response, which is exactly
 * the server-driven model `usePolicy` was written against. All this adapter does is attach
 * each policy to its entity and rename the abilities into Slate's vocabulary.
 */
import type {
  ApiClient,
  CollectionPatch,
  DocumentPatch,
  DocumentQuery,
  SculptRunRequest,
} from "~/api/client";
import { type ConversationStreamHandler, readEventStream } from "~/api/conversationStream";
import {
  ApiError,
  AutomationError,
  FlowValidationError,
  ForbiddenError,
  NotFoundError,
  SculptConfirmRequiredError,
  SculptDisabledError,
  SculptError,
  UnauthorizedError,
} from "~/api/errors";
import { createKnowItAi } from "~/api/knowitAi";
import { createKnowItAiAdmin } from "~/api/knowitAiAdmin";
import { createKnowItAutomation } from "~/api/knowitAutomation";
import { createKnowItCanopy, createKnowItKnowledgeDebug } from "~/api/knowitCanopy";
import { createKnowItComments } from "~/api/knowitComments";
import { createKnowItConversations } from "~/api/knowitConversations";
import { createKnowItEditor } from "~/api/knowitEditor";
import { createKnowItGit } from "~/api/knowitGit";
import { createKnowItGovernance } from "~/api/knowitGovernance";
import { createKnowItGrants } from "~/api/knowitGrants";
import { createKnowItIde } from "~/api/knowitIde";
import { createKnowItIntents } from "~/api/knowitIntents";
import { createKnowItLibrary } from "~/api/knowitLibrary";
import { createKnowItMail } from "~/api/knowitMail";
import { createKnowItMailReading } from "~/api/knowitMailReading";
import { createKnowItMerge } from "~/api/knowitMerge";
import { createKnowItNotifications } from "~/api/knowitNotifications";
import { createKnowItOps } from "~/api/knowitOps";
import { createKnowItProfiles } from "~/api/knowitProfiles";
import { createKnowItReasoning } from "~/api/knowitReasoning";
import { createKnowItWisdom } from "~/api/knowitWisdom";
import { createKnowItWork } from "~/api/knowitWork";
import type { CollectionPermission } from "~/api/libraryTypes";
import type {
  Abilities,
  Actor,
  ApiKey,
  Collection,
  Document,
  Group,
  Member,
  PublicProfileCv,
  Revision,
  SculptLatest,
  SculptOutcome,
  SculptStreamEvent,
  Session,
  Share,
  SharedDocument,
  Viewer,
  Workspace,
} from "~/api/types";
import { SculptLatestSchema, SculptOutcomeSchema } from "~/api/types";
import { createKnowItKnowledge } from "./knowitKnowledge";
import { createKnowItResearch } from "./knowitResearch";

export interface KnowItApiOptions {
  /** Empty = same origin; Caddy proxies /api/* to knowit-web-api. */
  baseUrl?: string;
  getAccessToken: (forceRefresh?: boolean) => Promise<string | null>;
  /** Full-page redirect into the identity provider; `returnTo` is where to land afterwards. */
  startSignIn: (returnTo?: string, provider?: string) => Promise<void>;
  /** Drop the local session. */
  endSession: () => void;
  onUnauthorized?: () => void;
  fetchImpl?: typeof fetch;
}

interface Envelope<T> {
  data: T;
  policies?: Array<{ id: string; abilities: Record<string, boolean> }>;
  pagination?: { offset: number; limit: number; total: number };
}

/**
 * KnowIt's ability names are not Slate's. Translating here — rather than renaming Slate's
 * constants — keeps the shell backend-agnostic: a different server maps its own names in its
 * own adapter and every component keeps reading `can.update`.
 */
const WORKSPACE_ABILITIES: Record<string, string> = {
  update: "update",
  manageUsers: "manageMembers",
  createDocument: "createDocument",
  createCollection: "createCollection",
  listApiKeys: "manageApiKeys",
  manage: "manageIntegrations",
  listViews: "viewAudit",
};

const COLLECTION_ABILITIES: Record<string, string> = {
  update: "update",
  delete: "delete",
  createDocument: "createDocument",
};

const DOCUMENT_ABILITIES: Record<string, string> = {
  update: "update",
  delete: "delete",
  share: "share",
  archive: "archive",
  unarchive: "restore",
  restore: "restore",
};

function translate(
  source: Record<string, boolean> | undefined,
  map: Record<string, string>,
): Abilities {
  const out: Abilities = {};
  if (!source) {
    return out;
  }
  for (const [from, to] of Object.entries(map)) {
    if (source[from] === true) {
      out[to] = true;
    }
  }
  return out;
}

/**
 * First line of prose, for list rows. Headings are skipped rather than stripped: a KnowIt
 * document opens with an H1 that repeats its title, so keeping it would print the title twice
 * on every row.
 */
function summarise(text: string | null | undefined): string {
  if (!text) {
    return "";
  }
  const line = text
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith("#"));
  if (!line) {
    return "";
  }
  return line.length > 140 ? `${line.slice(0, 139)}…` : line;
}

function actor(value: unknown): Actor {
  const source = (value ?? {}) as { id?: string; name?: string; avatarUrl?: string | null };
  return {
    id: source.id ?? "",
    name: source.name ?? "",
    avatarUrl: source.avatarUrl ?? null,
  };
}

/** KnowIt's `icon` is an icon *name* ("beaker") as often as an emoji; only keep emoji. */
function emojiOrNull(icon: unknown): string | null {
  return typeof icon === "string" && icon.length <= 4 && !/^[a-z-]+$/i.test(icon) ? icon : null;
}

/**
 * The run-control refusals whose wire `code` a caller branches on rather than string-matching
 * a translated sentence (W2 §B.3). Every one of them is a 400 — they never collide with the
 * 401/403/404 mapping — and each names a fix the surface can offer: `unknown_node` carries the
 * `knownNodes` the Jump dialog should have listed, `run_is_live` means "pause first",
 * `no_destination`/`bad_destination` are what the Publish dialog's two validation messages are
 * about. Deliberately a closed set: a code not listed here stays a plain `ApiError`, so nothing
 * else in the app silently changes shape.
 */
const RUN_CONTROL_ERROR_CODES = new Set([
  "unknown_node",
  "run_is_live",
  "no_destination",
  "bad_destination",
  "stepping_unsupported",
  "unsupported_for_kind",
]);

interface RawDocument {
  id: string;
  title?: string;
  icon?: string | null;
  /** Markdown source. The platform also sends ProseMirror JSON as `data`; Slate reads `text`. */
  text?: string | null;
  collectionId?: string | null;
  parentDocumentId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string | null;
  archivedAt?: string | null;
  deletedAt?: string | null;
  createdBy?: unknown;
  /** W6 — which editing lane this document is on (`ApiPresenters.cs:129`). Absent ⇒ "v1". */
  editorLane?: string | null;
}

interface RawCollection {
  id: string;
  name?: string;
  icon?: string | null;
  description?: string | null;
  index?: number | null;
  /** W5 — the project this collection is grouped under, if any (`ApiPresenters.cs:154`). */
  projectId?: string | null;
  /** W10 §8 — set once the collection is archived; every document in it is then hidden. */
  archivedAt?: string | null;
}

interface RawMember {
  id: string;
  name?: string;
  email?: string;
  avatarUrl?: string | null;
  role?: string;
  isSuspended?: boolean;
  lastActiveAt?: string | null;
}

interface RawGroupRow {
  id: string;
  name?: string;
  memberCount?: number;
}

/**
 * What a bodiless refusal means, in a sentence. HTTP/2 carries no reason phrase, so for a
 * response with no body this is the only readable thing left — and "" is never an acceptable
 * message to put in front of a person.
 */
function statusSentence(status: number): string {
  if (status === 400) return "The server refused that request";
  if (status === 401) return "Your session has expired";
  if (status === 403) return "You do not have access to that";
  if (status === 404) return "That is not available here";
  if (status === 409) return "That conflicts with something that already exists";
  if (status === 429) return "Too many requests — try again shortly";
  if (status === 502 || status === 503 || status === 504) {
    return "The server is restarting or unreachable — try again in a moment";
  }
  if (status >= 500) return "The server had a problem with that request";
  return `Request failed (${status})`;
}

export class KnowItApi implements ApiClient {
  private readonly baseUrl: string;
  private readonly getAccessToken: KnowItApiOptions["getAccessToken"];
  private readonly startSignIn: KnowItApiOptions["startSignIn"];
  private readonly endSession: KnowItApiOptions["endSession"];
  private readonly onUnauthorized: (() => void) | undefined;
  private readonly fetchImpl: typeof fetch;

  /** auth.info answers viewer(), workspaces.* and session() — fetch it once per burst. */
  private authInfo: { at: number; promise: Promise<Envelope<AuthInfoData>> } | null = null;

  constructor(options: KnowItApiOptions) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/$/, "");
    this.getAccessToken = options.getAccessToken;
    this.startSignIn = options.startSignIn;
    this.endSession = options.endSession;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private send<T>(
    path: string,
    body: Record<string, unknown> = {},
    retryOn401 = true,
  ): Promise<Envelope<T>> {
    return this.request<Envelope<T>>(
      path,
      { method: "POST", body: JSON.stringify(body) },
      retryOn401,
    );
  }

  /**
   * The RPC surface is all POST, but a handful of the platform's older routes are REST-shaped
   * GETs — and one of them (`thinkers`) *creates* a record on POST, so the method matters.
   */
  private async request<T>(path: string, init: RequestInit, retryOn401 = true): Promise<T> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      ...init,
      // same-origin keeps the platform's CookieToJwtMiddleware a drop-in alternative.
      credentials: "same-origin",
      headers: {
        ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401) {
      // One retry with a freshly refreshed token covers a token that expired in flight.
      if (retryOn401 && (await this.getAccessToken(true))) {
        return this.request<T>(path, init, false);
      }
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }
    if (!response.ok) {
      throw await this.errorOf(response);
    }

    return (await response.json()) as T;
  }

  /**
   * One refusal, one typed error — and the body read exactly once.
   *
   * `messageOf` keeps a refusal's sentence and throws its machine-readable `error` code away,
   * which is fine for "not found" but not for the run-control routes: `runs.jump` answers
   * `{error:"unknown_node", knownNodes:[…]}`, and the node list IS the fix the Jump dialog
   * offers. Those codes (all 400s) become an {@link AutomationError} with `.code`/`.knownNodes`
   * intact, exactly as FakeApi already throws; everything else maps by status as before, so no
   * existing `instanceof ForbiddenError`/`NotFoundError` branch changes meaning.
   */
  private async errorOf(response: Response): Promise<ApiError> {
    const { body, message } = await this.parseErrorBody(response);
    // The refusal's JSON rides on the error as `.body`: `work.plan.apply`'s `errors[]` and
    // `work.plan.approve`'s `changed[]` ARE the fix (readers: `planChangedRefusals`,
    // talk's `describeApplyError`), and a bare message loses them.
    return Object.assign(this.typedErrorOf(response, body, message), { body });
  }

  private typedErrorOf(response: Response, body: unknown, message: string): ApiError {
    const payload = (body ?? {}) as { message?: string; error?: string; knownNodes?: unknown };

    // A refused flow save carries its reasons in the BODY, and the reasons are the message
    // (W3 §E.3). Kept separate from the run-control set above because the payload shape is
    // different: a list, not a node roster.
    if (payload.error === "invalid_flow") {
      const strings = (value: unknown): string[] =>
        Array.isArray(value)
          ? value.filter((entry): entry is string => typeof entry === "string")
          : [];
      const body = (payload as { errors?: unknown; warnings?: unknown; result?: unknown }).result;
      const inner = (body ?? payload) as { errors?: unknown; warnings?: unknown };
      return new FlowValidationError(
        message,
        strings(inner.errors),
        strings(inner.warnings),
        response.status,
      );
    }
    if (typeof payload.error === "string" && RUN_CONTROL_ERROR_CODES.has(payload.error)) {
      const knownNodes = Array.isArray(payload.knownNodes)
        ? payload.knownNodes.filter((entry): entry is string => typeof entry === "string")
        : undefined;
      return new AutomationError(payload.error, message, response.status, knownNodes);
    }
    if (response.status === 403) {
      return new ForbiddenError(message);
    }
    if (response.status === 404) {
      return new NotFoundError(message);
    }
    return new ApiError(message, response.status);
  }

  /**
   * The `/api/v2/documents/*` sculpt routes answer bare objects (no `{ data, policies }`
   * envelope) and carry typed refusals the UI must keep: `sculpt_disabled` hides every
   * affordance, `confirm_required` carries the drift-fingerprint token a destructive apply
   * must be re-posted with, and a completion failure names the thinker that failed (B6).
   */
  private async sculptSend(path: string, init: RequestInit, retryOn401 = true): Promise<unknown> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      ...init,
      credentials: "same-origin",
      headers: {
        ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401) {
      if (retryOn401 && (await this.getAccessToken(true))) {
        return this.sculptSend(path, init, false);
      }
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }

    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // A bodiless refusal (a bare Forbid()) still maps by status below.
    }

    if (!response.ok) {
      throw sculptErrorFrom(response.status, body, response.statusText);
    }
    return body;
  }

  /**
   * The REST lane, for the platform's owner-built controllers (`api/thinkers`,
   * `api/local-thinkers`, `api/capability-policies`, `api/selection-actions`).
   *
   * It exists separately from `request` for one reason that has already cost this area: those
   * routes answer **204 No Content** for every remove and several writes, and `request`
   * unconditionally parses JSON — so a successful delete would land on the server and then
   * throw in the browser. Here an empty body is `null`, which is what a void verb means.
   *
   * Everything else is identical: bearer injected, one refresh-and-retry on 401, and the
   * server's own words on 403/404.
   */
  private async rest(
    path: string,
    method: string,
    body?: Record<string, unknown>,
    retryOn401 = true,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return (await this.restFull(path, method, body, retryOn401, signal)).body;
  }

  /**
   * The same lane, with the STATUS kept.
   *
   * W6: `ai-actions/transform-selection` answers **200 with the transformed text** or **202
   * with a dispatch id** when the resolved thinker is pinned to somebody's VS Code window.
   * Both are success and the bodies are different shapes, so the status is not decoration —
   * it is the discriminator. Refusals still throw here exactly as they do through `rest`,
   * except the two the v2 routes carry meaning in: a **403** from `/ai-action` is a lane
   * negotiation, and a **409** `not_on_v2_lane` names the state, so both keep their codes via
   * the ordinary error mapping below.
   */
  private async restFull(
    path: string,
    method: string,
    body?: Record<string, unknown>,
    retryOn401 = true,
    signal?: AbortSignal,
  ): Promise<{ status: number; body: unknown }> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });

    if (response.status === 401) {
      if (retryOn401 && (await this.getAccessToken(true))) {
        return this.restFull(path, method, body, false, signal);
      }
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }
    if (response.status === 403) {
      throw new ForbiddenError(await this.messageOf(response));
    }
    if (response.status === 404) {
      throw new NotFoundError(await this.messageOf(response));
    }
    if (!response.ok) {
      // The v2 lane's refusals name themselves (`not_on_v2_lane`, `instructions_required`,
      // `thinker_unavailable`), and string-matching a translated message is how that branch
      // rots — so keep the wire code the way the sculpt lane already does.
      const { body: payload, message } = await this.parseErrorBody(response);
      const code = (payload as { error?: unknown } | null)?.error;
      if (typeof code === "string" && code.length > 0) {
        throw new SculptError(code, message, response.status);
      }
      throw new ApiError(message, response.status);
    }
    if (response.status === 204) {
      return { status: response.status, body: null };
    }
    // A 200 with an empty body is the same nothing as a 204; neither is a parse failure.
    return { status: response.status, body: await response.json().catch(() => null) };
  }

  /**
   * W9 — status + JSON body, without throwing on a non-2xx. `documents.merge.commit`'s 409 body
   * is DATA (`{error, unresolved}` — the only place that count is delivered, §E-7), so this
   * exists precisely to hand the body back even when the response is not `ok`. One 401 retry
   * still applies; every other status is the caller's to interpret.
   */
  private async postStatus(
    path: string,
    body: Record<string, unknown> = {},
    retryOn401 = true,
  ): Promise<{ status: number; body: unknown }> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (response.status === 401) {
      if (retryOn401 && (await this.getAccessToken(true))) {
        return this.postStatus(path, body, false);
      }
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }
    return { status: response.status, body: await response.json().catch(() => null) };
  }

  /**
   * The `text/event-stream` lane (W6 — `ai-actions/refine-selection`).
   *
   * Identical framing to `sculptStream`: `data: {json}\n\n`, terminated by `data: [DONE]`,
   * a chunk may end mid-frame so the tail is kept, and a torn frame is dropped rather than
   * aborting the stream. Every failable check runs server-side before the first byte, so a
   * non-OK status here is still a whole, typed refusal.
   */
  private async sse(
    path: string,
    body: Record<string, unknown>,
    onFrame: (payload: unknown) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

    if (!response.ok || !response.body) {
      const { body: payload, message } = await this.parseErrorBody(response);
      const code = (payload as { error?: unknown } | null)?.error;
      throw typeof code === "string" && code.length > 0
        ? new SculptError(code, message, response.status)
        : new ApiError(message, response.status);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        for (const line of frame.split("\n")) {
          if (!line.startsWith("data:")) {
            continue;
          }
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") {
            return;
          }
          try {
            onFrame(JSON.parse(payload));
          } catch {
            // A torn frame is dropped rather than aborting the stream.
          }
        }
      }
    }
  }

  /**
   * The OTHER `text/event-stream` lane (W8 §0.5): `event: <name>\ndata: <json>\n\n`, no
   * `[DONE]` sentinel, nulls omitted. The frame grammar lives in `conversationStream.ts` so
   * the chunk-boundary case is testable without a socket; this method's only job is the
   * bearer, the pre-stream refusal, and handing over the body.
   *
   * ⚠ W8-21: the server writes the SSE headers BEFORE the auth check, so an unauthorized call
   * arrives as an `error` FRAME inside HTTP 200. `response.ok` cannot catch it — the reader
   * turns that frame into a rejection instead.
   */
  private async eventStream(
    path: string,
    body: Record<string, unknown>,
    onEvent: ConversationStreamHandler,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

    if (response.status === 401) {
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }
    if (!response.ok || !response.body) {
      const { message } = await this.parseErrorBody(response);
      throw new ApiError(message, response.status);
    }
    return readEventStream(response.body, onEvent, signal);
  }

  /**
   * Status + body, without throwing. Exactly one caller needs it — the AI provider gate, whose
   * whole job is to tell `503 provider_not_configured` apart from every other refusal. Turning
   * that into an exception would collapse the distinction the gate exists to draw.
   */
  private async probe(path: string, method: string): Promise<{ status: number; body: unknown }> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return { status: response.status, body: await response.json().catch(() => null) };
  }

  private async messageOf(response: Response): Promise<string> {
    return (await this.parseErrorBody(response)).message;
  }

  /**
   * One body read, three shapes tolerated (§E-13). `UsersController`/`GroupsController`
   * answer a 400 with a bare **string** body (`"Cannot change your own role."`) — valid JSON,
   * but `{ message?, error? }` property access on a string reads as `undefined`, so the old
   * `response.json().then(p => p.message ?? p.error ?? statusText)` silently dropped it and
   * every clean-room refusal toasted "Bad Request". This also falls back to the raw text for a
   * body that is not JSON at all (never an HTML error page, which is never the message).
   */
  private async parseErrorBody(response: Response): Promise<{ body: unknown; message: string }> {
    // A real fetch Response's body can be read exactly once, so `.text()` — when the object
    // has one — is the single read, parsed as JSON afterwards; some test doubles across this
    // package model only `.json()`, so that stays the fallback rather than a second, redundant
    // read a real Response would reject with "body stream already read".
    let text: string | null = null;
    if (typeof response.text === "function") {
      try {
        text = await response.text();
      } catch {
        // A bodiless refusal (billing.usage's bare Forbid()) still maps by status below.
      }
    }

    let body: unknown = null;
    if (text !== null) {
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          // Not JSON — the trimmed text itself may still be the message (see below).
        }
      }
    } else {
      try {
        body = await response.json();
      } catch {
        // No body at all, or a test double with neither method — statusText is all there is.
      }
    }

    if (typeof body === "string" && body.trim()) {
      return { body, message: body.trim() };
    }
    if (body && typeof body === "object") {
      const payload = body as { message?: string; error?: string };
      return { body, message: payload.message ?? payload.error ?? response.statusText };
    }
    if (text?.trim() && !/^\s*</.test(text)) {
      return { body, message: text.trim().slice(0, 300) };
    }
    // A bodiless refusal has only `statusText` to fall back on — and over HTTP/2 that is ALWAYS
    // the empty string (the protocol dropped the reason phrase). Every deployed surface here is
    // HTTP/2 behind Caddy, so a bare `NotFound()` / `Forbid()` / proxy 502 reached the UI as an
    // error with NO MESSAGE AT ALL, and screens rendered an empty sentence or invented one.
    // Synthesise the reason from the status instead: it is the only thing the response carries.
    return { body, message: response.statusText || statusSentence(response.status) };
  }

  private policyFor(envelope: Envelope<unknown>, id: string): Record<string, boolean> | undefined {
    return envelope.policies?.find((policy) => policy.id === id)?.abilities;
  }

  private toDocument(raw: RawDocument, envelope: Envelope<unknown>): Document {
    return {
      id: raw.id,
      title: raw.title || "Untitled",
      emoji: emojiOrNull(raw.icon),
      summary: summarise(raw.text),
      text: raw.text ?? "",
      collectionId: raw.collectionId ?? null,
      parentDocumentId: raw.parentDocumentId ?? null,
      createdAt: raw.createdAt ?? new Date(0).toISOString(),
      updatedAt: raw.updatedAt ?? raw.createdAt ?? new Date(0).toISOString(),
      publishedAt: raw.publishedAt ?? null,
      archivedAt: raw.archivedAt ?? null,
      deletedAt: raw.deletedAt ?? null,
      createdBy: actor(raw.createdBy),
      views: 0,
      // W6: which editor opens this document. `documents.info` serves the v1 baseline TEXT for
      // a v2 document regardless, so the scene must branch on the lane, not on the body.
      editorLane: raw.editorLane === "v2" ? "v2" : "v1",
      abilities: translate(this.policyFor(envelope, raw.id), DOCUMENT_ABILITIES),
    };
  }

  private toCollection(raw: RawCollection, envelope: Envelope<unknown>): Collection {
    return {
      id: raw.id,
      name: raw.name || "Untitled",
      emoji: emojiOrNull(raw.icon),
      description: raw.description ?? "",
      index: raw.index ?? 0,
      projectId: raw.projectId ?? null,
      archivedAt: raw.archivedAt ?? null,
      abilities: translate(this.policyFor(envelope, raw.id), COLLECTION_ABILITIES),
    };
  }

  /** Shared by `members.list` and every `people.*` write that echoes back a `User` row. */
  private toMember(raw: RawMember, envelope: Envelope<unknown>): Member {
    return {
      id: raw.id,
      name: raw.name ?? "",
      email: raw.email ?? "",
      avatarUrl: raw.avatarUrl ?? null,
      role: raw.role ?? "member",
      suspended: raw.isSuspended === true,
      lastActiveAt: raw.lastActiveAt ?? null,
      abilities: translate(this.policyFor(envelope, raw.id), {
        update: "update",
        delete: "delete",
      }),
    };
  }

  /** Shared by `groups.list` and every `people.groups.*` write that echoes back a `Group` row. */
  private toGroup(raw: RawGroupRow, envelope: Envelope<unknown>): Group {
    return {
      id: raw.id,
      name: raw.name ?? "",
      memberCount: raw.memberCount ?? 0,
      abilities: translate(this.policyFor(envelope, raw.id), {
        update: "update",
        delete: "delete",
      }),
    };
  }

  private info(): Promise<Envelope<AuthInfoData>> {
    const now = Date.now();
    if (!this.authInfo || now - this.authInfo.at > 5_000) {
      this.authInfo = { at: now, promise: this.send<AuthInfoData>("auth.info") };
    }
    return this.authInfo.promise;
  }

  auth = {
    // Slate's own sign-in is OIDC; this exists only to satisfy the interface for adapters
    // that use a password form.
    login: async (): Promise<Session> => {
      throw new ApiError("This workspace signs in through your identity provider", 400);
    },

    signIn: async (returnTo?: string, provider?: string): Promise<void> => {
      await this.startSignIn(returnTo, provider);
    },

    logout: async (): Promise<void> => {
      this.authInfo = null;
      this.endSession();
    },

    session: async (): Promise<Session | null> => {
      try {
        const envelope = await this.info();
        return {
          userId: envelope.data.user.id,
          workspaceId: envelope.data.team.id,
          createdAt: envelope.data.user.createdAt ?? new Date().toISOString(),
        };
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          return null;
        }
        throw error;
      }
    },
  };

  async viewer(): Promise<Viewer> {
    const { data } = await this.info();
    return {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      avatarUrl: data.user.avatarUrl ?? null,
      // KnowIt stores "en_US"; Slate's catalogues are keyed on the base tag.
      language: (data.user.language ?? "en").split(/[-_]/)[0] ?? "en",
      role: data.user.role ?? "member",
    };
  }

  workspaces = {
    /**
     * `auth.workspaces` rather than auth.info's `availableTeams`: it is the endpoint the
     * platform's own picker uses and it carries what a picker needs — which one is current,
     * which auto-enters, which are suspended, and when each was last opened.
     */
    list: async (): Promise<Workspace[]> => {
      const envelope = await this.info();
      const response = await this.send<never>("auth.workspaces");
      const raw = response as unknown as {
        workspaces?: Array<{
          teamId: string;
          name?: string;
          avatarUrl?: string | null;
          current?: boolean;
          suspended?: boolean;
          autoEnter?: boolean;
          lastSelectedAt?: string | null;
        }>;
      };

      return (raw.workspaces ?? []).map((workspace) => ({
        id: workspace.teamId,
        name: workspace.name ?? "",
        subdomain: null,
        avatarUrl: workspace.avatarUrl ?? null,
        current: workspace.current === true,
        suspended: workspace.suspended === true,
        autoEnter: workspace.autoEnter === true,
        lastSelectedAt: workspace.lastSelectedAt ?? null,
        // Preferences, like abilities, are per-session: `auth.info` carries only the CURRENT
        // team's document, and inventing an empty one for the others is honest — nothing may
        // merge into a workspace it cannot read.
        preferences: workspace.current ? (envelope.data.team.preferences ?? {}) : {},
        // Abilities are per-session, so only the current workspace has a policy to attach.
        abilities: workspace.current
          ? translate(this.policyFor(envelope, workspace.teamId), WORKSPACE_ABILITIES)
          : {},
      }));
    },

    current: async (): Promise<Workspace> => {
      const envelope = await this.info();
      const team = envelope.data.team;
      return {
        id: team.id,
        name: team.name,
        subdomain: team.subdomain ?? null,
        avatarUrl: null,
        current: true,
        suspended: false,
        autoEnter: false,
        lastSelectedAt: null,
        // The whole preference document, untyped and unfiltered — §E-5's merge reads from it.
        preferences: team.preferences ?? {},
        abilities: translate(this.policyFor(envelope, team.id), WORKSPACE_ABILITIES),
      };
    },

    /**
     * Exactly what the platform's own switcher does: POST auth.switchWorkspace, which
     * re-scopes the session **cookie** for this origin, then hard-reload.
     *
     * The bearer token must be dropped first. Its `tid` claim is fixed at issuance, and the
     * platform's CookieToJwtMiddleware only reads the cookie when no Authorization header is
     * present — so keeping the token would silently pin the app to the old workspace while
     * the server believed it had switched.
     *
     * The server's `redirect` points at the platform's own host; we reload our own origin
     * instead. Every query cache is scoped to the old workspace, so a reload is the honest
     * reset — the same reason the platform does it.
     */
    switch: async (
      workspaceId: string,
      options: { rememberAutoEnter?: boolean; returnTo?: string } = {},
    ): Promise<void> => {
      await this.send("auth.switchWorkspace", {
        teamId: workspaceId,
        ...(options.rememberAutoEnter === undefined
          ? {}
          : { rememberAutoEnter: options.rememberAutoEnter }),
      });

      this.authInfo = null;
      this.endSession();
      // A deep link (a work plan, a tile) survives the switch via returnTo. Same-origin
      // paths only — "//host" would be a cross-origin redirect wearing a path's clothes.
      const returnTo = options.returnTo;
      const samePagePath = returnTo?.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/";
      window.location.replace(samePagePath);
    },

    /**
     * W4 §E-5. Delegated to the ops module on purpose: `health.saveSettings` already owns the
     * one read-modify-write against `teams.update`, and a second implementation of the same
     * merge is how one of them ends up posting a bare patch and wiping the document.
     *
     * `authInfo` is invalidated afterwards so the next `workspaces.current()` reads back the
     * preferences that were actually stored, rather than the burst-cached ones from before.
     */
    updatePreferences: async (patch: Record<string, unknown>): Promise<Record<string, unknown>> => {
      const merged = await this.ops.health.updatePreferences(patch);
      this.authInfo = null;
      return merged;
    },
  };

  /**
   * W10 §10 — a bearer-carrying MULTIPART post that tolerates an empty body.
   *
   * Not `research.upload`: that one calls `response.json()` unconditionally, and
   * `attachments.upload` answers **204 with no body** — parsing it would throw on the success
   * path. Like every multipart lane here it sets Accept + bearer only, because stamping
   * Content-Type json over a FormData body destroys the boundary the browser generated.
   */
  private async formPost(path: string, body: FormData): Promise<unknown> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
    });
    if (response.status === 401) {
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }
    if (!response.ok) {
      throw await this.errorOf(response);
    }
    if (response.status === 204) {
      return null;
    }
    return (await response.json().catch(() => null)) as unknown;
  }

  /**
   * W10 §5 — a bearer-carrying GET whose body is BYTES.
   *
   * `fetch` follows the platform's 302 to the signed storage URL by default, so this resolves
   * the artefact itself. It cannot be an `<a href>` or a `window.open`: this app's session is a
   * PKCE bearer, not a cookie, and a browser-initiated navigation carries no Authorization
   * header — it would arrive anonymous and 401 (the World-page outage of 2026-08-18).
   */
  private async blob(path: string): Promise<Blob> {
    const token = await this.getAccessToken();
    const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (response.status === 401) {
      this.onUnauthorized?.();
      throw new UnauthorizedError("Your session has expired");
    }
    if (!response.ok) {
      throw await this.errorOf(response);
    }
    return response.blob();
  }

  /** Bare-JSON research surfaces — same bearer token + 401-refresh as every enveloped call. */
  research = {
    ...createKnowItResearch({
      get: (path) => this.request(path, { method: "GET" }),
      post: (path, body, signal) =>
        this.request(path, { method: "POST", body: JSON.stringify(body), signal }),
    }),
    post: (path: string, body: unknown): Promise<unknown> =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body) }),
    // Multipart: the shared `request` would stamp Content-Type json over the form boundary,
    // so this lane sets ONLY Accept + bearer and lets the browser own the rest.
    upload: async (path: string, form: FormData): Promise<unknown> => {
      const token = await this.getAccessToken();
      const response = await this.fetchImpl(`${this.baseUrl}/api/${path}`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      });
      if (!response.ok) {
        throw new ApiError(`Upload failed (${response.status})`, response.status);
      }
      return (await response.json()) as unknown;
    },
  };

  collections = {
    /**
     * W10 §8 — archived collections are HIDDEN unless `statusFilter` asks for them; there is no
     * `collections.archived` endpoint. The filter is additive server-side (`includeArchived`
     * only drops the `ArchivedAt == null` clause), so asking for archived returns BOTH — the
     * caller separates them on `archivedAt`.
     */
    list: async (options: { includeArchived?: boolean } = {}): Promise<Collection[]> => {
      const envelope = await this.send<RawCollection[]>("collections.list", {
        limit: 100,
        ...(options.includeArchived ? { statusFilter: ["archived"] } : {}),
      });
      return envelope.data.map((raw) => this.toCollection(raw, envelope));
    },

    get: async (id: string): Promise<Collection> => {
      const envelope = await this.send<RawCollection>("collections.info", { id });
      return this.toCollection(envelope.data, envelope);
    },

    create: async (data: { name: string }): Promise<Collection> => {
      const envelope = await this.send<RawCollection>("collections.create", { name: data.name });
      return this.toCollection(envelope.data, envelope);
    },

    update: async (id: string, data: CollectionPatch): Promise<Collection> => {
      const envelope = await this.send<RawCollection>("collections.update", {
        id,
        ...(data.name === undefined ? {} : { name: data.name }),
        ...(data.description === undefined ? {} : { description: data.description }),
      });
      return this.toCollection(envelope.data, envelope);
    },

    delete: async (id: string): Promise<void> => {
      await this.send("collections.delete", { id });
    },

    /** Archiving hides every document in the collection — gated on `Delete`, not `Update`. */
    archive: async (id: string): Promise<Collection> => {
      const envelope = await this.send<RawCollection>("collections.archive", { id });
      return this.toCollection(envelope.data, envelope);
    },

    restore: async (id: string): Promise<Collection> => {
      const envelope = await this.send<RawCollection>("collections.restore", { id });
      return this.toCollection(envelope.data, envelope);
    },

    /**
     * Delegated, not duplicated — and through a getter, because `libraryApi` is declared below
     * this field and a direct reference would read it before initialisation. (Class field
     * initialisers run in declaration order; a getter defers the read to call time.)
     */
    people: {
      list: (collectionId: string) => this.libraryApi.collectionPeople.list(collectionId),
      add: (input: { collectionId: string; userId: string; permission: CollectionPermission }) =>
        this.libraryApi.collectionPeople.add(input),
      remove: (input: { collectionId: string; userId: string }) =>
        this.libraryApi.collectionPeople.remove(input),
      groups: (collectionId: string) => this.libraryApi.collectionPeople.groups(collectionId),
      addGroup: (input: {
        collectionId: string;
        groupId: string;
        permission: CollectionPermission;
      }) => this.libraryApi.collectionPeople.addGroup(input),
      removeGroup: (input: { collectionId: string; groupId: string }) =>
        this.libraryApi.collectionPeople.removeGroup(input),
    },
  };

  documents = {
    list: async (query: DocumentQuery = {}): Promise<Document[]> => {
      const limit = query.limit ?? 25;

      if (query.query?.trim()) {
        return this.search(query.query);
      }

      // Drafts, archive and trash are separate endpoints here, but they stay one concept in
      // the UI — the status filter picks the endpoint.
      const endpoint =
        query.status === "draft"
          ? "documents.drafts"
          : query.status === "archived"
            ? "documents.archived"
            : query.status === "deleted"
              ? "documents.deleted"
              : "documents.list";

      const envelope = await this.send<RawDocument[]>(endpoint, {
        limit,
        ...(query.collectionId ? { collectionId: query.collectionId } : {}),
        ...(query.parentDocumentId ? { parentDocumentId: query.parentDocumentId } : {}),
      });
      return envelope.data.map((raw) => this.toDocument(raw, envelope));
    },

    get: async (id: string): Promise<Document> => {
      // Alone among the documents.* endpoints, documents.info nests its payload one level
      // down — `{ data: { document } }` — where every sibling returns the document as `data`
      // directly. Mapping `data` itself produced a Document with every field undefined, which
      // is why an opened document rendered as an empty "Untitled".
      const envelope = await this.send<{ document: RawDocument } | RawDocument>("documents.info", {
        id,
      });
      const raw = "document" in envelope.data ? envelope.data.document : envelope.data;
      return this.toDocument(raw, envelope);
    },

    create: async (data: {
      title: string;
      collectionId?: string | null;
      /** W7: the prompt bar's `create-document` and the starter templates both send a body. */
      text?: string;
      /** Defaults to true WITH a collection (a draft has nowhere to be published to). */
      publish?: boolean;
    }): Promise<Document> => {
      const envelope = await this.send<RawDocument>("documents.create", {
        title: data.title,
        ...(data.text === undefined ? {} : { text: data.text }),
        ...(data.collectionId
          ? { collectionId: data.collectionId, publish: data.publish ?? true }
          : data.publish === undefined
            ? {}
            : { publish: data.publish }),
      });
      return this.toDocument(envelope.data, envelope);
    },

    update: async (id: string, data: DocumentPatch): Promise<Document> => {
      const envelope = await this.send<RawDocument>("documents.update", {
        id,
        ...(data.title === undefined ? {} : { title: data.title }),
      });
      return this.toDocument(envelope.data, envelope);
    },

    /**
     * W10 §1. `documents.move` wraps its payload one level deeper than every sibling —
     * `{ data: { documents: [doc] } }` — the same nesting `documents.duplicate` uses. Reading
     * `envelope.data` here would hand the mapper an object with no `id` and produce an
     * "Untitled" document, which is exactly the bug `documents.info` once had.
     */
    move: async (input: {
      id: string;
      collectionId?: string;
      parentDocumentId?: string;
    }): Promise<Document> => {
      const envelope = await this.send<{ documents?: RawDocument[] } | RawDocument>(
        "documents.move",
        {
          id: input.id,
          ...(input.collectionId === undefined ? {} : { collectionId: input.collectionId }),
          ...(input.parentDocumentId === undefined
            ? {}
            : { parentDocumentId: input.parentDocumentId }),
        },
      );
      const raw =
        "documents" in envelope.data && Array.isArray(envelope.data.documents)
          ? envelope.data.documents[0]
          : (envelope.data as RawDocument);
      if (!raw?.id) {
        throw new ApiError("The server moved the document but returned no row", 502);
      }
      return this.toDocument(raw, envelope);
    },

    publish: async (id: string): Promise<Document> => {
      const envelope = await this.send<RawDocument>("documents.update", { id, publish: true });
      return this.toDocument(envelope.data, envelope);
    },

    /**
     * W10 §4 — the real route. Publish has none (`documents.update {publish:true}` is it), but
     * UNpublish does, and it is the only way to take a document off the published list.
     */
    unpublish: async (id: string): Promise<Document> => {
      const envelope = await this.send<RawDocument>("documents.unpublish", { id });
      return this.toDocument(envelope.data, envelope);
    },

    archive: async (id: string): Promise<Document> => {
      const envelope = await this.send<RawDocument>("documents.archive", { id });
      return this.toDocument(envelope.data, envelope);
    },

    restore: async (id: string): Promise<Document> => {
      const envelope = await this.send<RawDocument>("documents.restore", { id });
      return this.toDocument(envelope.data, envelope);
    },

    delete: async (id: string): Promise<void> => {
      await this.send("documents.delete", { id });
    },

    permanentlyDelete: async (id: string): Promise<void> => {
      await this.send("documents.delete", { id, permanent: true });
    },

    sculpt: async (id: string, request: SculptRunRequest): Promise<SculptOutcome> => {
      const body = await this.sculptSend(`v2/documents/${id}/sculpt`, {
        method: "POST",
        body: sculptRequestBody(request, false),
      });
      return SculptOutcomeSchema.parse(body);
    },

    sculptStream: async (
      id: string,
      request: SculptRunRequest,
      onEvent: (event: SculptStreamEvent) => void,
      signal?: AbortSignal,
    ): Promise<void> => {
      const token = await this.getAccessToken();
      const response = await this.fetchImpl(`${this.baseUrl}/api/v2/documents/${id}/sculpt`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: sculptRequestBody(request, true),
        ...(signal ? { signal } : {}),
      });

      // Every failable check runs server-side before the first byte, so a non-OK status
      // here is still a whole, typed refusal — after this point errors arrive as frames.
      if (!response.ok || !response.body) {
        let payload: unknown = null;
        try {
          payload = await response.json();
        } catch {
          // fall through to the status-only mapping
        }
        throw sculptErrorFrom(response.status, payload, response.statusText);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        // SSE frames are `data: {json}\n\n`; a chunk may end mid-frame, so keep the tail.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";
        for (const frame of frames) {
          for (const line of frame.split("\n")) {
            if (!line.startsWith("data:")) {
              continue;
            }
            const payload = line.slice(5).trim();
            if (payload === "[DONE]") {
              return;
            }
            try {
              onEvent(JSON.parse(payload) as SculptStreamEvent);
            } catch {
              // A torn frame is dropped rather than aborting the stream.
            }
          }
        }
      }
    },

    sculptLatest: async (id: string): Promise<SculptLatest | null> => {
      try {
        const body = await this.sculptSend(`v2/documents/${id}/sculpt/latest`, { method: "GET" });
        // Never sculpted answers 204 — a bodiless success, not a row to parse.
        if (body === null || body === undefined) {
          return null;
        }
        return SculptLatestSchema.parse(body);
      } catch (error) {
        // Older servers answered 404 for the same ordinary state.
        if (error instanceof NotFoundError) {
          return null;
        }
        throw error;
      }
    },

    sculptResolve: async (
      id: string,
      opId: string,
      action: "accept" | "reject" | "discard",
    ): Promise<SculptOutcome> => {
      const body = await this.sculptSend(`v2/documents/${id}/sculpt/${opId}/resolve`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });
      return SculptOutcomeSchema.parse(body);
    },

    sculptRevert: async (id: string, opId: string): Promise<SculptOutcome> => {
      const body = await this.sculptSend(`v2/documents/${id}/sculpt/${opId}/revert`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      return SculptOutcomeSchema.parse(body);
    },

    revisions: async (id: string): Promise<Revision[]> => {
      const envelope = await this.send<
        Array<{
          id: string;
          title?: string;
          text?: string;
          createdAt?: string;
          createdBy?: unknown;
        }>
      >("revisions.list", { documentId: id, limit: 25 });

      return envelope.data.map((raw) => ({
        id: raw.id,
        documentId: id,
        title: raw.title || "Untitled",
        summary: summarise(raw.text),
        createdAt: raw.createdAt ?? new Date(0).toISOString(),
        createdBy: actor(raw.createdBy),
      }));
    },
  };

  async search(query: string): Promise<Document[]> {
    // documents.search returns match records wrapping the document.
    const envelope = await this.send<Array<{ document?: RawDocument } & RawDocument>>(
      "documents.search",
      { query, limit: 25 },
    );
    return envelope.data
      .map((entry) => entry.document ?? entry)
      .filter((raw): raw is RawDocument => Boolean(raw?.id))
      .map((raw) => this.toDocument(raw, envelope));
  }

  members = {
    list: async (): Promise<Member[]> => {
      const envelope = await this.send<RawMember[]>("users.list", { limit: 100 });
      return envelope.data.map((raw) => this.toMember(raw, envelope));
    },
  };

  groups = {
    /**
     * `GroupsController.List` answers `data: { groups: [...], groupMemberships: [...] }` — an
     * OBJECT, not the bare array every sibling `*.list` route returns. `envelope.data.map(...)`
     * threw `TypeError: envelope.data.map is not a function` against the real backend (§E-9,
     * fixed as step 1 of this wave; pinned by a golden fixture in `knowit.test.ts`).
     * `groupMemberships` here is only the first 8 members per group (`MaxAvatarDisplay`) —
     * enough for an avatar strip, never a roster; `groups.memberships` is the real roster
     * (`people.groups.members`, §A.6.2).
     */
    list: async (): Promise<Group[]> => {
      const envelope = await this.send<{ groups: RawGroupRow[] }>("groups.list", { limit: 100 });
      return envelope.data.groups.map((raw) => this.toGroup(raw, envelope));
    },
  };

  apiKeys = {
    list: async (): Promise<ApiKey[]> => {
      const envelope = await this.send<
        Array<{
          id: string;
          name?: string;
          secret?: string;
          last4?: string;
          createdAt?: string;
          lastActiveAt?: string | null;
        }>
      >("apiKeys.list", { limit: 100 });

      return envelope.data.map((raw) => ({
        id: raw.id,
        name: raw.name ?? "",
        // Never surface a full secret: show only the tail the server exposes.
        partialSecret: raw.last4 ? `…${raw.last4}` : "…",
        createdAt: raw.createdAt ?? new Date(0).toISOString(),
        lastActiveAt: raw.lastActiveAt ?? null,
        abilities: translate(this.policyFor(envelope, raw.id), { delete: "revoke" }),
      }));
    },

    create: async (data: { name: string }): Promise<ApiKey> => {
      const envelope = await this.send<{
        id: string;
        name?: string;
        last4?: string;
        createdAt?: string;
      }>("apiKeys.create", { name: data.name });
      return {
        id: envelope.data.id,
        name: envelope.data.name ?? data.name,
        partialSecret: envelope.data.last4 ? `…${envelope.data.last4}` : "…",
        createdAt: envelope.data.createdAt ?? new Date().toISOString(),
        lastActiveAt: null,
        abilities: { revoke: true },
      };
    },

    revoke: async (id: string): Promise<void> => {
      await this.send("apiKeys.delete", { id });
    },
  };

  shares = {
    list: async (): Promise<Share[]> => {
      const envelope = await this.send<
        Array<{
          id: string;
          documentId?: string;
          documentTitle?: string;
          url?: string;
          includeChildDocuments?: boolean;
          views?: number;
          createdAt?: string;
          createdBy?: unknown;
        }>
      >("shares.list", { limit: 100 });

      return envelope.data.map((raw) => ({
        id: raw.id,
        documentId: raw.documentId ?? "",
        documentTitle: raw.documentTitle ?? "Untitled",
        url: raw.url ?? "",
        includeChildren: raw.includeChildDocuments === true,
        views: raw.views ?? 0,
        createdAt: raw.createdAt ?? new Date(0).toISOString(),
        createdBy: actor(raw.createdBy),
        abilities: translate(this.policyFor(envelope, raw.id), {
          revoke: "revoke",
          update: "revoke",
        }),
      }));
    },

    revoke: async (id: string): Promise<void> => {
      await this.send("shares.revoke", { id });
    },
  };

  /**
   * Flows, runs, schedules, agents and activity. The automation endpoints answer with their own
   * shapes rather than the `{ data, policies }` envelope, so the translation lives in its own
   * module and is handed a transport instead of reaching into this class.
   */
  automation = createKnowItAutomation({
    post: (path, body) => this.send<unknown>(path, body),
    get: (path) => this.request<unknown>(path, { method: "GET" }),
    workspaceAbilities: async () => {
      const envelope = await this.info();
      return translate(this.policyFor(envelope, envelope.data.team.id), WORKSPACE_ABILITIES);
    },
  });

  /**
   * The central AI ledger (`ai.usage`, `ai.calls`, `queues.digest`). Bespoke shapes like the
   * automation routes, so the translation lives in `knowitAi.ts` and rides the same
   * bearer-injected transport — never a raw fetch, which would arrive anonymous.
   */
  ai = createKnowItAi({ post: (path, body) => this.send<unknown>(path, body) });

  /** Work (Docit Work) — bespoke envelopes over the same bearer-injected transport. */
  work = createKnowItWork({ post: (path, body) => this.send<unknown>(path, body) });

  reasoning = createKnowItReasoning({
    post: (path, body) => this.send<unknown>(path, body),
    get: (path) => this.request<unknown>(path, { method: "GET" }),
  });

  /** Git connections — the same transport; a token goes in once and never comes back. */
  git = createKnowItGit({ post: (path, body) => this.send<unknown>(path, body) });
  mail = createKnowItMail({ post: (path, body) => this.send<unknown>(path, body) });
  mailReading = createKnowItMailReading({ post: (path, body) => this.send<unknown>(path, body) });

  /**
   * AI administration (thinkers, capability policies, AI actions). REST rather than RPC, and
   * bare rather than enveloped, so it rides `rest()` — which keeps the method honest
   * (`GET /api/thinkers` reads; POSTing there would CREATE) and tolerates the 204s.
   */
  private readonly aiAdmin = createKnowItAiAdmin({
    get: (path) => this.rest(path, "GET"),
    post: (path, body, signal) => this.rest(path, "POST", body ?? {}, true, signal),
    put: (path, body) => this.rest(path, "PUT", body ?? {}),
    del: (path) => this.rest(path, "DELETE"),
    probe: (path, method) => this.probe(path, method),
  });

  thinkers = this.aiAdmin.thinkers;
  capabilities = this.aiAdmin.capabilities;
  aiActions = this.aiAdmin.aiActions;

  /**
   * W6 — the v2 editing lane, selection AI, mentions and live widget values.
   *
   * Four transports because this wave's endpoints genuinely differ: `rest` is the bare-JSON
   * 204-tolerant lane the `v2/documents/*` and `ai-actions/*` routes ride; `restStatus` is the
   * same lane with the status kept, because `transform-selection`'s MEANING is in its status
   * code (200 text vs **202 dispatch**); `rpc` is Outline dot-RPC for `suggestions.mention`;
   * and `stream` is the SSE lane `refine-selection` answers on. `authInfoRaw` hands over
   * `auth.info`'s untyped data, where the collaboration token rides.
   */
  private readonly editorApi = createKnowItEditor({
    rest: (path, method, body) => this.rest(path, method, body),
    restStatus: (path, method, body) => this.restFull(path, method, body),
    rpc: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
    stream: (path, body, onFrame, signal) => this.sse(path, body, onFrame, signal),
    authInfoRaw: async () => {
      const envelope = await this.info();
      return envelope.data as unknown as Record<string, unknown>;
    },
  });

  editorV2 = this.editorApi.editorV2;
  selection = this.editorApi.selection;
  mentions = this.editorApi.mentions;
  quotes = this.editorApi.quotes;

  /**
   * Flow health, document tasks and nudges (W2). `authInfoRaw` hands back `auth.info`'s
   * untyped `data` object — `health.saveSettings` needs the WHOLE current preferences JSON
   * (F-1: `teams.update` replaces it wholesale), not just the fields `AuthInfoData` declares.
   */
  private readonly ops = createKnowItOps({
    post: (path, body) => this.send<unknown>(path, body),
    authInfoRaw: async () => {
      const envelope = await this.info();
      return envelope.data as unknown as Record<string, unknown>;
    },
  });

  health = this.ops.health;
  documentTasks = this.ops.documentTasks;
  nudges = this.ops.nudges;

  /**
   * W7 — `ai-actions/taskize` and `ai-actions/notify`. They ride the 204-tolerant bare-JSON
   * REST lane (not dot-RPC), which is also what keeps their refusal codes intact.
   */
  private readonly intentsApi = createKnowItIntents({
    rest: (path, method, body) => this.rest(path, method, body),
  });

  intents = this.intentsApi.intents;

  /**
   * W10 — stars, pins, share writes, filtered search, collection permissions, templates,
   * exports and attachments. All Outline-era dot-RPC with the `{data, policies}` envelope, so
   * one transport does; `toDocument` is handed over because a starred/pinned/searched row must
   * carry the SAME translated abilities as the same document read anywhere else.
   */
  private readonly libraryApi = createKnowItLibrary({
    post: (path, body) => this.send<unknown>(path, body ?? {}),
    // `shares.info` answers 204 for "never shared" — the enveloped lane cannot parse that.
    postTolerant: (path, body) => this.rest(path, "POST", body ?? {}),
    blob: (path) => this.blob(path),
    // Multipart rides the same lane `research.upload` proved: Accept + bearer only, because
    // stamping Content-Type json over a FormData body destroys its boundary.
    form: (path, body) => this.formPost(path, body),
    toDocument: (raw, envelope) =>
      this.toDocument(raw as RawDocument, envelope as Envelope<unknown>),
  });

  stars = this.libraryApi.stars;
  pins = this.libraryApi.pins;

  /** W10 §4 — the share dialog's writes. `shares.list`/`revoke` stay on `shares` above. */
  shareLinks = this.libraryApi.shareLinks;

  /** W10 §5 — the document export job and its artefact. */
  exports = this.libraryApi.exports;

  /** W10 §6 — filtered search + the viewer's recent searches. */
  searches = this.libraryApi.searches;

  /** W10 §10 — the two-step attachment upload behind the editor's image block. */
  attachments = this.libraryApi.attachments;

  /**
   * `templates.ensureStarters` — idempotent server-side, by title, over the team's templates.
   * Enveloped `{data, policies}` like the rest of the documents controller.
   */
  templates = {
    ensureStarters: async (): Promise<Document[]> => {
      const envelope = await this.send<RawDocument[]>("templates.ensureStarters", {});
      return envelope.data.map((raw) => this.toDocument(raw, envelope));
    },
    list: (): Promise<Document[]> => this.libraryApi.templates.list(),
    createFrom: (input: {
      documentId: string;
      title: string;
      text: string;
      collectionId?: string | null;
    }): Promise<Document> => this.libraryApi.templates.createFrom(input),
    promote: (input: { documentId: string; collectionId?: string }): Promise<Document> =>
      this.libraryApi.templates.promote(input),
    createDocument: (input: {
      templateId: string;
      title?: string;
      collectionId?: string | null;
    }): Promise<Document> => this.libraryApi.templates.createDocument(input),
  };

  /**
   * W4: connected editor windows, dispatch writes, access grants, documentation sync, the
   * remote-session conversation store and the first-run wizard.
   *
   * Three transports, because this wave's endpoints genuinely differ: `post` is the dot-RPC
   * lane (`ide-sessions.*`, `ide-access.*`, `ai-dispatch.*` — bare or `{data}`, never
   * `{data, policies}`), `get` is the REST lane where the METHOD is the meaning
   * (`doc-sync/links` reads; `doc-sync/link` writes), and `rest` is the 204-tolerant lane the
   * conversation store's POST rides. All three carry the bearer — a raw `fetch` would arrive
   * anonymous, which is how the World page went dark on 2026-08-18.
   */
  private readonly ideApi = createKnowItIde({
    post: (path, body) => this.send<unknown>(path, body),
    get: (path) => this.request<unknown>(path, { method: "GET" }),
    rest: (path, method, body) => this.rest(path, method, body),
  });

  ide = this.ideApi.ide;
  docSync = this.ideApi.docSync;
  wizard = this.ideApi.wizard;

  /**
   * W8 — the document AI layer: `api/tomatoflow/knowit/thoughts` (the turn face, plus BDI) and
   * `api/document-conversations` (the lifecycle face). Everything there is BARE and several
   * verbs answer 204, so both ride `rest()`; the turn stream is a THIRD SSE framing
   * (`event:` lines, nulls omitted) and gets its own transport.
   */
  private readonly conversationsApi = createKnowItConversations({
    rest: (path, method, body) => this.rest(path, method, body),
    stream: (path, body, onEvent, signal) => this.eventStream(path, body, onEvent, signal),
  });

  /** W4's remote-session store and W8's document AI layer share one namespace by design. */
  conversations = { ...this.ideApi.conversations, ...this.conversationsApi };

  /**
   * W8 S9/S10 — the two ENVELOPED families. `comments.*` is `{data, policies}` (the only one
   * in the wave) and `documents.chat.*` is `{data}`, so both ride `send()` rather than the
   * bare `rest()` lane every other conversation route uses.
   */
  private readonly commentsApi = createKnowItComments({
    rpc: (path, body) => this.send<unknown>(path, body),
  });

  comments = this.commentsApi.comments;
  documentChat = this.commentsApi.documentChat;

  /**
   * W5: projects, Members/Groups writes, and workspace governance. `rpc` is `send()` (Outline
   * dot-RPC, `{data[, policies]}`); `rest` is the same 204-tolerant REST lane `aiAdmin` rides —
   * it is also what `knowitauth.*`/`analytics/organizations` need, since a proxied Auth 204
   * (delete team/role/policy, `updateUserAttributes`) would otherwise throw parsing an empty
   * body as JSON.
   */
  private readonly governanceApi = createKnowItGovernance({
    rpc: <T>(path: string, body?: Record<string, unknown>) => this.send<T>(path, body),
    rest: (path, method, body) => this.rest(path, method, body),
    viewer: async () => {
      const viewer = await this.viewer();
      return { email: viewer.email, name: viewer.name, avatarUrl: viewer.avatarUrl };
    },
  });

  projects = this.governanceApi.projects;
  people = this.governanceApi.people;
  governance = this.governanceApi.governance;

  /** CNPY-7/8 — the knowledge canopy. Bare-JSON `canopy.*`. */
  canopy = createKnowItCanopy({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  });

  /** Desk `/knowledge/debug` — `knowledge.debug*`, admin-only bare JSON. */
  knowledgeDebug = createKnowItKnowledgeDebug({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  });

  /**
   * Workspace Knowledge — `knowledge.status|activity` are GETs that throw on failure;
   * `knowledge.refresh|refresh.cancel` ride `postStatus`, because a 409/503/403 body there is
   * the answer (who is already running, why the server refused), not an exception.
   */
  knowledge = createKnowItKnowledge({
    get: (path) => this.request<unknown>(path, { method: "GET" }),
    postStatus: (path, body) => this.postStatus(path, body),
  });

  /** W9 §A.1 — the wisdom graph. Bare-JSON `wisdom.*` plus the one REST survivor, `experts`. */
  wisdom = createKnowItWisdom({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
    get: (path) => this.request<unknown>(path, { method: "GET" }),
  });

  /**
   * W9 §A.2 — merge, revision parts and restore. `post` rides the generic `request` lane
   * (not `send`) because `documents.revision.restore` answers three top-level keys, not a
   * pure `{data}` envelope — `knowitMerge.ts` reads the raw body itself. `commitStatus` is the
   * one call that must see a 409 body rather than have it thrown away (§E-7).
   */
  merge = createKnowItMerge({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
    commitStatus: (sessionId) => this.postStatus("documents.merge.commit", { sessionId }),
  });

  /**
   * W9 §A.3 — share grants. The three anonymous `*.viaGrant` reads ride this SAME transport:
   * with no PKCE session, `getAccessToken()` resolves `null` and no `Authorization` header is
   * sent, so the public door works unauthenticated with zero special-casing.
   */
  grants = createKnowItGrants({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  });

  /** W9 §A.4 — the owner's half of `profiles.*` (`public.profile` below stays the anonymous read). */
  profiles = createKnowItProfiles({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  });

  /**
   * W9 §A.5 — the inbox and preferences. `put` rides `rest()` for the two ownership-checked
   * REST twins; `authInfoRaw` is where `user.notificationSettings` rides.
   */
  notifications = createKnowItNotifications({
    post: (path, body) =>
      this.request<unknown>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
    put: (path, body) => this.rest(path, "PUT", body ?? {}),
    authInfoRaw: async () => {
      const envelope = await this.info();
      return envelope.data as unknown as Record<string, unknown>;
    },
  });

  // SLATE-LATER: the platform serves public *shares* from its own origin, so that one is not
  // reachable through this adapter yet. It throws rather than pretend.
  public = {
    share: async (): Promise<SharedDocument> => {
      throw new NotFoundError("Public share links are served by the platform, not by Slate");
    },
    /**
     * `profiles.getPublic` is `[AllowAnonymous]`, so this rides the ordinary transport with
     * whatever token exists (none, for a visitor). Like the rest of the `profiles.*` family it
     * wraps its payload in `data` — unwrap it, or the scene gets an all-`undefined` object and
     * prints an empty CV instead of the 404 empty state. A private profile and an unknown slug
     * are the SAME 404 by construction (AC-3.4.3); nothing here may distinguish them.
     */
    profile: async (slug: string): Promise<PublicProfileCv> => {
      const response = (await this.research.post("profiles.getPublic", { slug })) as {
        data: PublicProfileCv;
      };
      return response.data;
    },
  };
}

function sculptRequestBody(request: SculptRunRequest, stream: boolean): string {
  return JSON.stringify({
    op: request.op,
    idempotencyKey: request.idempotencyKey,
    ...(request.instructions === undefined ? {} : { instructions: request.instructions }),
    ...(request.params === undefined ? {} : { params: request.params }),
    ...(request.requestedLane === undefined ? {} : { requestedLane: request.requestedLane }),
    ...(request.confirmToken === undefined ? {} : { confirmToken: request.confirmToken }),
    ...(stream ? { stream: true } : {}),
  });
}

/** Maps the sculpt routes' typed refusals onto Slate's error vocabulary. */
function sculptErrorFrom(status: number, body: unknown, fallback: string): ApiError {
  const payload = (body ?? {}) as {
    error?: string;
    message?: string;
    confirmToken?: string;
    scope?: { partsReplaced?: number; summary?: string };
    thinkerName?: string;
    thinkerId?: string;
  };

  if (payload.error === "sculpt_disabled") {
    return new SculptDisabledError();
  }
  if (payload.error === "confirm_required" && typeof payload.confirmToken === "string") {
    return new SculptConfirmRequiredError(
      payload.confirmToken,
      payload.scope?.partsReplaced ?? 0,
      payload.scope?.summary ?? "Confirmation required",
    );
  }
  if (payload.error === "completion_failed") {
    // B6: the failing thinker is named, or the message collapses back into "AI is broken".
    const who = payload.thinkerName || payload.thinkerId || "AI";
    return new ApiError(`${who}: ${payload.message ?? "completion failed"}`, status);
  }

  const message = payload.message ?? payload.error ?? fallback;
  if (status === 403) {
    return new ForbiddenError(message);
  }
  if (status === 404) {
    return new NotFoundError(message);
  }
  // Keep the wire code: apply_lane_unsupported is the one callers resolve by re-asking on
  // the propose lane, and string-matching a translated message is how that branch rots.
  if (typeof payload.error === "string" && payload.error.length > 0) {
    return new SculptError(payload.error, message, status);
  }
  return new ApiError(message, status);
}

interface AuthInfoData {
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string | null;
    language?: string;
    role?: string;
    createdAt?: string;
  };
  team: {
    id: string;
    name: string;
    subdomain?: string | null;
    /** `ApiPresenters` emits the preference document as parsed JSON, not a string. */
    preferences?: Record<string, unknown>;
  };
  availableTeams: Array<{ id: string; name: string }>;
  /**
   * W6 — the collaboration JWT for the Hocuspocus spine. HS256, signed with
   * `KNOWIT_INTERNAL_JWT_SECRET`, `sub` = user id, 24-hour TTL. NOT this client's PKCE bearer:
   * an RS256 access token is rejected by the collab server. The wire has always carried it
   * (`AuthController.cs:156`); this interface simply did not declare it.
   */
  collaborationToken?: string | null;
}
